import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    IconButton,
    Switch,
    Chip,
    Stack,
    Tooltip,
    Typography,
    Grid,
    Card,
    Divider,
    CardContent,
    CardHeader,
    FormControlLabel,
    Autocomplete,
    TextField,
    Alert
} from '@mui/material';
import {
    AddCircle,
    CheckCircle,
    Block, DeleteOutlined, ArrowForward
} from '@mui/icons-material';

const ObjectConditions = ({ value, onChange, availableObjects, update = false }) => {
    const [objects, setObjects] = useState(value.objects || []);
    const [editingIndex, setEditingIndex] = useState(null);
    const [error, setError] = useState('');
    const [filteredDetectors, setFilteredDetectors] = useState([]);

    useEffect(() => {
        if (editingIndex !== null) {
            if (objects[editingIndex].object.value[0] == 'D') {
                // base parent is the first in tags
                let childTag = objects[editingIndex].object.tags[0];
                console.log('selected', objects[editingIndex]);
                console.log(availableObjects);
                // filter all the available objects that have the base parent = child tag
                let filtered = availableObjects.filter(obj => obj.tags && obj.tag.id == childTag.id);
                if (filtered.length > 0) {
                    // add a null entry first with value text null
                    filtered.unshift({value: null, text: 'No selection', tags: {id: null}});
                }
                setFilteredDetectors(filtered);
                console.log('filtered', filtered);
            } else {
                setFilteredDetectors([]);

            }
        }
    }, [availableObjects, editingIndex, objects[editingIndex]]);

    useEffect(() => {
        setObjects(value.objects || []);
    }, [value.objects]);

    const startEditing = (index) => setEditingIndex(index);

    const handleChange = (field, value) => {
        try {
            const updated = [...objects];

            if (field === 'value') {
                updated[editingIndex] = { ...updated[editingIndex], [field]: value, object: availableObjects[value], detector:null };
            } if (field === 'detector')
            {
                if (filteredDetectors[value].value === null) {
                    // delete the detector entirely
                    updated[editingIndex] = { ...updated[editingIndex], detector: null };
                } else {
                    updated[editingIndex] = {...updated[editingIndex], detector: filteredDetectors[value]};
                }
            }else {
                updated[editingIndex] = { ...updated[editingIndex], [field]: value };
            }
            setObjects(updated);
            setError('');
        } catch (err) {
            setError('Error updating object');
        }
    };

    const handleAdd = () => {
        try {
            setObjects([...objects, { value: 0, missing: false, object: availableObjects[0]??null,detector: null  }]);
            setEditingIndex(objects.length);
            setError('');
        } catch (err) {
            setError('Error adding object');
        }
    };

    const handleDelete = (index) => {
        try {
            setObjects(objects.filter((_, i) => i !== index));
            if (editingIndex === index) setEditingIndex(null);
            setError('');
        } catch (err) {
            setError('Error deleting object');
        }
    };

    const handleSave = () => {
            // check if we have duplicate object values and set errot not set editing index
            let values = objects.map(obj => obj.object.value+'|'+(obj.detector?.value??''));
            if (values.length !== new Set(values).size) {
                setError('Duplicate object values '+ objects[editingIndex].object.text);
                return;
            }

        setEditingIndex(null);
    }

    const displayItems = useMemo(() => objects.filter((_, i) => i !== editingIndex), [objects, editingIndex,availableObjects]);

    useEffect(() => {
        let hasMissingObjects = objects.some(obj => obj.missing);

        onChange({
            objects,
            hasMissingObjects
        });
    }, [objects, onChange, availableObjects]);

    if (update) {
        return (<div>Loading</div>);
    }

    return (
        <Card variant="outlined" p={1}>
            <CardHeader title="Object Conditions" />
            <CardContent sx={{ paddingBottom: "14px" }}>
                {error && <Alert severity="error">{error}</Alert>}
                {availableObjects.length === 0 && (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        📝 No Object Conditions to display here. Change the selected zone or add Clase to the flux.
                    </Typography>
                )}
                <Divider sx={{ marginBottom: "15px" }} />
                {availableObjects.length > 0 && (
                    <Box>
                        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 2 }}>
                            {displayItems.length > 0 && displayItems.map((obj, i) => (
                                <Tooltip title={obj.missing ? 'Missing' : 'Present'} key={(obj.missing ? 'Missing' : 'Present')+i+obj.object.tag.id}>
                                    <>
                                    {editingIndex===null &&  <Chip
                                        label={
                                            <>
                                                {obj.missing && <Block sx={{ fontSize: '10px', mr: 0.5}} />}
                                                {availableObjects[obj.value]?.text ?? ''}
                                                {obj.detector && <><ArrowForward sx={{ fontSize: '10px', mr: 1, ml:1}} />{obj.detector.text}</>}
                                            </>
                                        }
                                        onClick={() => startEditing(i)}
                                        onDelete={() => handleDelete(i)}
                                        color={obj.missing ? 'warning' : 'primary'}
                                        variant="outlined"
                                        size="small"
                                        sx={{ 'borderColor':obj.object.tag.color}}
                                    />}
                                    {editingIndex!==null &&  <Chip
                                        label={
                                            <>
                                                {obj.missing && <Block sx={{ fontSize: '10px', mr: 0.5 }} />}
                                                {availableObjects[obj.value]?.text ?? ''}
                                                {obj.detector && <><ArrowForward sx={{ fontSize: '10px', mr: 1, ml:1}} />{obj.detector.text}</>}
                                            </>
                                        }
                                        color={obj.missing ? 'warning' : 'primary'}
                                        variant="outlined"
                                        size="small"
                                        sx={{ 'borderColor':obj.object.tag.color}}
                                    />}
                                    </>
                                </Tooltip>
                            ))}
                        </Stack>

                        {editingIndex !== null && (
                            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <Grid item xs={2}>
                                    <Tooltip title="Toggle Missing">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={objects[editingIndex]?.missing}
                                                    onChange={(e) => handleChange('missing', e.target.checked)}
                                                />
                                            }
                                            label="Missing"
                                        />
                                    </Tooltip>
                                </Grid>

                                <Grid item xs={8}>
                                    <Autocomplete
                                        size="small"
                                        options={availableObjects}
                                        getOptionLabel={(option) => option.text}
                                        value={availableObjects[objects[editingIndex]?.value] || null}
                                        onChange={(_, newValue) => {
                                            const index = availableObjects.findIndex(obj => obj.value === newValue?.value);
                                            handleChange('value', index);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Class" fullWidth />}
                                    />
                                    {filteredDetectors.length>0 &&
                                    <Autocomplete
                                        sx={{ mt: 2 }}
                                        size="small"
                                        options={filteredDetectors}
                                        getOptionLabel={(option) => option.text}
                                        value={objects[editingIndex]?.detector || null}
                                        onChange={(_, newValue) => {
                                            const index = filteredDetectors.findIndex(obj => obj.value === newValue?.value);
                                            handleChange('detector', index);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Detector in Detector" fullWidth />}
                                    />}
                                </Grid>

                                <Grid item xs={1}>
                                    <IconButton onClick={handleSave} color="primary">
                                        <CheckCircle />
                                    </IconButton>
                                </Grid>

                                <Grid item xs={1}>
                                    <IconButton onClick={() => handleDelete(editingIndex)} color="error">
                                        <DeleteOutlined />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        )}

                        {objects.length > 0 && <Divider />}
                        <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                            {objects.length === 0 && (
                                <Chip label="No Objects added yet" size="small" color="primary" variant="outlined" />
                            )}
                            <Button onClick={handleAdd} startIcon={<AddCircle />} sx={{ mb: 1 }} size="small">
                                Add Object
                            </Button>
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default ObjectConditions;
